import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { formatCurrency } from "@/lib/formatters";
import { AdminListing, ListingStatus } from "@/lib/types/adminTypes";
import { AdminStatusBadge } from "./AdminStatusBadge";
import { ListingActionsDropdown } from "./ListingActionsDropdown";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch"; // Import Switch
import { useAdminListingMutations } from "@/hooks/useAdminListings"; // Import useAdminListingMutations

interface ListingTableRowProps {
  listing: AdminListing;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onViewListing: (id: string) => void;
  onEditListing: (id: string) => void;
  onDeleteListing: (id: string) => void;
  onStatusChange: (id: string, status: ListingStatus) => void;
}

export function ListingTableRow({
  listing,
  isSelected,
  onSelect,
  onViewListing,
  onEditListing,
  onDeleteListing,
  onStatusChange,
}: ListingTableRowProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { updateListing } = useAdminListingMutations(); // Use the mutation hook

  const handleFeatureToggle = async (checked: boolean) => {
    try {
      await updateListing.mutateAsync({
        id: listing.id.toString(),
        data: { is_featured: checked },
      });
    } catch (error) {
      console.error("Failed to update featured status:", error);
      // Optionally, show a toast error here
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  return (
    <TableRow className={isMenuOpen ? "z-10 relative" : ""}>
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect(listing.id.toString())}
          aria-label="Select listing"
        />
      </TableCell>
      <TableCell className="font-medium">
        {listing.title || "Untitled"}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {listing.user?.name || "Unknown"}
      </TableCell>
      <TableCell>{formatCurrency(listing.price)}</TableCell>
      <TableCell className="hidden lg:table-cell">
        {formatDate(listing.created_at)}
      </TableCell>
      <TableCell>
        <AdminStatusBadge status={listing.status} />
      </TableCell>
      <TableCell>
        <Switch
          checked={!!listing.is_featured} // Convert to boolean
          onCheckedChange={handleFeatureToggle}
          aria-label="Toggle featured status"
        />
      </TableCell>
      <TableCell>
        <ListingActionsDropdown
          listingId={listing.id.toString()}
          status={listing.status}
          onView={() => onViewListing(listing.id.toString())}
          onEdit={() => onEditListing(listing.id.toString())}
          onDelete={() => onDeleteListing(listing.id.toString())}
          onStatusChange={onStatusChange}
          onOpenChange={setIsMenuOpen}
        />
      </TableCell>
    </TableRow>
  );
}
